import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  Button,
  Typography,
  Zoom,
  IconButton,
  useTheme,
  useMediaQuery,
  Divider,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ListOutlinedIcon from "@mui/icons-material/ListOutlined";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import EmojiNatureIcon from "@mui/icons-material/EmojiNature";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import WineBarIcon from "@mui/icons-material/WineBar";
import CircularProgress from "@mui/material/CircularProgress";
import YouTube from "react-youtube";
import AudioComponent from "./AudioComponent";

function App() {
  const [mappa, setMappa] = useState(null);
  const [selectedPoi, setSelectedPoi] = useState();
  const [selectedPoiIndex, setSelectedPoiIndex] = useState();
  const [hoveredPoi, setHoveredPoi] = useState();
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [originalDimensions, setOriginalDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [isFullscreen, setIsFullscreen] = useState(true);
  const [touchStartTime, setTouchStartTime] = useState(0);
  const [touchStartPosition, setTouchStartPosition] = useState({ x: 0, y: 0 });

  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("md"));
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isPoiList, setIsPoiList] = useState(false);

  // sviluppo
  // const URL_SERVER = "http://localhost:8080/api";
  // const URL_APIMAPPA = "http://localhost:8080/api/mappe/full/";

  // produzione
  const URL_SERVER = "https://mappenarranti.ondalago.it/api";
  const URL_APIMAPPA = "https://mappenarranti.ondalago.it/api/mappe/full/";

  const urlParams = new URLSearchParams(window.location.search);
  const idMappa = urlParams.get("idMappa") || 1;
  const playerRef = useRef(null);

  useEffect(() => {
    fetch(`${URL_APIMAPPA}${idMappa}`)
      .then((response) => response.json())
      .then((data) => {
        setMappa(data);
      });
  }, []);

  useEffect(() => {
    if (mappa) {
      const img = new Image();
      img.src = `${URL_SERVER}/files${mappa.immagine}`;
      img.onload = () => {
        setOriginalDimensions({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });

        const initialZoom = calculateInitialZoom(
          img.naturalWidth,
          img.naturalHeight
        );
        setZoom(initialZoom);

        // Centra l'immagine
        if (containerRef.current) {
          const containerWidth = containerRef.current.offsetWidth;
          const scaledImageWidth = img.naturalWidth * initialZoom;

          const newPanX = (containerWidth - scaledImageWidth) / 2;
          const newPanY = 0;

          setPan({ x: newPanX, y: newPanY });
        }
      };
    }
  }, [mappa]);

  useEffect(() => {
    adjustPan();
  }, [zoom]);

  useEffect(() => {
    handleZoomReset();
  }, [isFullscreen]);

  useEffect(() => {
    if (mappa) {
      if (selectedPoi) {
        const index = mappa.pois.findIndex((poi) => poi.id === selectedPoi.id);
        setSelectedPoiIndex(index);
      } else {
        setSelectedPoiIndex(null);
      }
    }
  }, [selectedPoi, mappa]);

  const handleExit = () => {
    window.location.href = "https://ondalago.it/mappe-narranti/";
  };

  const handleNextPoi = () => {
    if (selectedPoiIndex !== null) {
      const nextIndex = (selectedPoiIndex + 1) % mappa.pois.length;
      setSelectedPoi(mappa.pois[nextIndex]);
    }
  };

  const handlePreviousPoi = () => {
    if (selectedPoiIndex !== null) {
      const previousIndex =
        (selectedPoiIndex - 1 + mappa.pois.length) % mappa.pois.length;
      setSelectedPoi(mappa.pois[previousIndex]);
    }
  };

  const calculateInitialZoom = (imgWidth: number, imgHeight: number) => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const containerHeight = containerRef.current.offsetHeight;

      // if (!mobile) {
      const widthRatio = containerWidth / imgWidth;
      // return widthRatio;
      // }
      // if (mobile) {
      const heightRatio = containerHeight / imgHeight;
      // return heightRatio;
      // }
      // Usa il rapporto minore per assicurarsi che l'immagine si adatti completamente
      return Math.max(widthRatio, heightRatio);
    }
    return 1; // Valore di default
  };

  /**
   * Adjusts the pan position to ensure the image is fully visible when it is
   * smaller than the container.
   */
  const adjustPan = () => {
    if (containerRef.current && imageRef.current) {
      let containerWidth = containerRef.current.offsetWidth;
      if (isPanelOpen && isFullscreen) {
        containerWidth = containerWidth * 0.6;
      }

      const containerHeight = containerRef.current.offsetHeight;
      const imageWidth = imageRef.current.width;
      const imageHeight = imageRef.current.height;

      let newX = pan.x;
      let newY = pan.y;

      // Prevent white space on left and right
      if (pan.x < 0) {
        newX = Math.max(containerWidth - imageWidth, newX);
      } else {
        newX = Math.max(containerWidth - imageWidth, Math.min(0, newX));
      }
      if (pan.y < 0) {
        newY = Math.max(containerHeight - imageHeight, newY);
      } else {
        newY = Math.max(containerHeight - imageHeight, Math.min(0, newY));
      }

      setPan({ x: newX, y: newY });
    }
  };

  const handlePoiClick = (poi) => {
    setIsFullscreen(true);
    setSelectedPoi(poi);
    setIsPanelOpen(true);
    setIsPoiList(false);
    if (!mobile) {
      setPan({ x: -(window.innerWidth * 0.2), y: 0 });
    }
  };

  const handleClosePanel = () => {
    setIsPanelOpen(false);
    setSelectedPoi(null);
    if (!mobile) {
      setPan({ x: 1, y: 1 });
    }
  };

  const handleZoomIn = () => {
    setZoom((prevZoom) => Math.min(prevZoom + 0.1, 3));
    adjustPan();
  };

  const handleZoomOut = () => {
    const containerWidth = containerRef.current.offsetWidth;
    const imageWidth = imageRef.current.offsetWidth * zoom;

    if (imageWidth > containerWidth) {
      setZoom((prevZoom) => Math.max(prevZoom - 0.1, 0.5));
    }
    adjustPan();
  };

  const handleZoomReset = () => {
    const initialZoom = calculateInitialZoom(
      originalDimensions.width,
      originalDimensions.height
    );
    setZoom(initialZoom);

    // Centra l'immagine
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const scaledImageWidth = originalDimensions.width * initialZoom;

      const newPanX = (containerWidth - scaledImageWidth) / 2;
      const newPanY = 0;

      setPan({ x: newPanX, y: newPanY });
    }
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    setPan({ x: newX, y: newY });
  };

  const handleMouseUp = (e) => {
    e.preventDefault();
    setIsDragging(false);
    adjustPan();
  };

  const handleMouseLeave = (e) => {
    if (isDragging) {
      e.preventDefault();
      setIsDragging(false);
      adjustPan();
    }
  };

  const handleTouchStart = (e) => {
    setTouchStartTime(Date.now());
    setTouchStartPosition({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    setIsDragging(true);
    setDragStart({
      x: e.touches[0].clientX - pan.x,
      y: e.touches[0].clientY - pan.y,
    });
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const newX = e.touches[0].clientX - dragStart.x;
    const newY = e.touches[0].clientY - dragStart.y;
    setPan({ x: newX, y: newY });
  };

  const handleTouchEnd = (e) => {
    setIsDragging(false);
    adjustPan();

    // Verifica se è un tap o un drag
    const touchEndTime = Date.now();
    const touchEndPosition = {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY,
    };

    const timeDiff = touchEndTime - touchStartTime;
    const distanceMoved = Math.sqrt(
      Math.pow(touchEndPosition.x - touchStartPosition.x, 2) +
        Math.pow(touchEndPosition.y - touchStartPosition.y, 2)
    );

    // Se il touch è durato meno di 200ms e si è spostato di meno di 10px, consideriamolo un tap
    if (timeDiff < 200 && distanceMoved < 10) {
      // Qui puoi gestire il tap, ad esempio:
      // handleTap(e);
    }
  };

  const handleToggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handlePoiList = () => {
    if (isPoiList) {
      setIsPoiList(false);
      setIsPanelOpen(false);
      if (!mobile) {
        setPan({ x: 1, y: 1 });
      }
    } else {
      setIsPoiList(true);
      setIsPanelOpen(true);
      if (!mobile) {
        setPan({ x: -(window.innerWidth * 0.2), y: 0 });
      }
    }
  };

  const InfoPoi = (label, value) => {
    return (
      <>
        <Typography variant="h6" sx={titoloSezioneStyle}>
          {label}
        </Typography>
        <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
          {value}
        </Typography>
      </>
    );
  };

  const typographyStyle = {
    paddingTop: 1,
    color: "#808080",
    fontWeight: "bold",
    fontSize: "0.9rem",
    textTransform: "uppercase",
    fontFamily: "inherit",
  };

  const titoloSezioneStyle = {
    color: "#008080",
    fontWeight: "bold",
    fontSize: "1.1rem",
    textTransform: "uppercase",
    fontFamily: "inherit",
  };

  const titoloListStyle = {
    color: "#008080",
    // fontWeight: "bold",
    fontSize: "1rem",
    textTransform: "uppercase",
    fontFamily: "inherit",
  };

  return (
    <>
      {!mappa && (
        <Box
          position="absolute"
          left="50%"
          top="50%"
          transform="translate(-50%, -50%)"
        >
          <CircularProgress
            size={60}
            thickness={5}
            color="primary"
            style={{ color: "#008080" }}
          />
        </Box>
      )}
      {mappa && (
        <Box sx={{ padding: "10px" }}>
          <Box
            position="relative"
            width="100%"
            height={mobile ? "300px" : "600px"}
            bgcolor={"#f5f5f5"}
            ref={containerRef}
            overflow="hidden"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            sx={{
              cursor: isDragging ? "grabbing" : "grab",
              height: isFullscreen ? "100vh" : mobile ? "300px" : "600px",
              position: isFullscreen ? "fixed" : "relative",
              top: isFullscreen ? 0 : "auto",
              left: isFullscreen ? 0 : "auto",
              width: isFullscreen ? "100vw" : "100%",
              zIndex: isFullscreen ? 1000 : "auto",
            }}
          >
            <Box
              position="absolute"
              width={`${originalDimensions.width * zoom}px`}
              height={`${originalDimensions.height * zoom}px`}
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px)`,
                transition: isDragging ? "none" : "transform 0.3s ease-out",
              }}
            >
              <img
                ref={imageRef}
                src={`${URL_SERVER}/files${mappa.immagine}`}
                alt={mappa.nome}
                style={{
                  width: "100%",
                  height: "auto",
                  objectFit: "contain",
                  pointerEvents: "none",
                }}
              />
              {isFullscreen &&
                mappa.pois.map((poi, index) => (
                  <Box
                    key={poi.id}
                    position="absolute"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePoiClick(poi);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseEnter={() => setHoveredPoi(poi)}
                    onMouseLeave={() => setHoveredPoi(null)}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                    }}
                    onTouchEnd={(e) => {
                      e.stopPropagation();
                      handlePoiClick(poi);
                    }}
                    sx={{
                      left: `${poi.posizioneX * zoom}px`,
                      top: `${poi.posizioneY * zoom}px`,
                      transform: "translate(-50%, -50%)", // Centra il POI sul punto esatto
                      cursor: "pointer",
                      "&:hover": {
                        transform: "translate(-50%, -50%) scale(1.2)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "38px",
                        height: "38px",
                        borderRadius: "50%",
                        borderColor:
                          poi.tipoId === 10
                            ? "#CE2127"
                            : poi.tipoId === 11
                            ? "#61B45A"
                            : poi.tipoId === 12
                            ? "#4AD6F7"
                            : poi.tipoId === 13
                            ? "#8A22CB"
                            : "#EEBE15",
                        borderWidth: "4px",
                        borderStyle: "solid",
                        backgroundColor:
                          selectedPoi?.id === poi.id ? "#CCCCCC" : "#FFFFFF",
                        color: "#666666",
                        "&:hover": {
                          backgroundColor:
                            selectedPoi?.id === poi.id ? "#CCCCCC" : "#FFFFFF",
                        },
                        transition: "background-color 0.3s ease",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        boxShadow: "0px 0px 2px rgba(0,0,0,0.5)",
                      }}
                    >
                      {poi.tipoId === 10 && (
                        <WineBarIcon
                          color="inherit"
                          fontSize="medium"
                          sx={{
                            filter:
                              "drop-shadow(0px 0px 2px rgba(255,255,255,0.5))",
                            color: "#CE2127",
                          }}
                        />
                      )}
                      {poi.tipoId === 11 && (
                        <EmojiNatureIcon
                          color="inherit"
                          fontSize="medium"
                          sx={{
                            filter:
                              "drop-shadow(0px 0px 2px rgba(255,255,255,0.5))",
                            color: "#61B45A",
                          }}
                        />
                      )}
                      {poi.tipoId === 12 && (
                        <AutoStoriesIcon
                          color="inherit"
                          fontSize="medium"
                          sx={{
                            filter:
                              "drop-shadow(0px 0px 2px rgba(255,255,255,0.5))",
                            color: "#4AD6F7",
                          }}
                        />
                      )}
                      {poi.tipoId === 13 && (
                        <SportsTennisIcon
                          color="inherit"
                          fontSize="medium"
                          sx={{
                            filter:
                              "drop-shadow(0px 0px 2px rgba(255,255,255,0.5))",
                            color: "#8A22CB",
                          }}
                        />
                      )}
                      {poi.tipoId === 14 && (
                        <LocalActivityIcon
                          color="inherit"
                          fontSize="medium"
                          sx={{
                            filter:
                              "drop-shadow(0px 0px 2px rgba(255,255,255,0.5))",
                            color: "#EEBE15",
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                ))}
              {isFullscreen &&
                mappa.pois.map((poi, index) => (
                  <Zoom in={hoveredPoi === poi}>
                    <Paper
                      elevation={3}
                      sx={{
                        position: "absolute",
                        left: `${
                          poi.posizioneX * zoom + 0.05 * poi.posizioneX * zoom
                        }px`,
                        top: `${
                          poi.posizioneY * zoom - 0.1 * poi.posizioneY * zoom
                        }px`,
                        transform: "translate(-300%, -300%)", // Centra il POI sul punto esatto
                        padding: "5px 10px",
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        zIndex: "tooltip",
                        width: "max-content",
                        maxWidth: "200px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <Typography variant="body2" noWrap>
                        {poi.titolo}
                      </Typography>
                    </Paper>
                  </Zoom>
                ))}
            </Box>
            <Box
              position="absolute"
              top="10px"
              left="10px"
              bgcolor="rgba(0, 128, 128, 0.7)"
              borderRadius="4px"
              padding="5px"
              sx={{ color: "white" }}
            >
              <IconButton
                onClick={handleExit}
                size="small"
                sx={{
                  "&, & *": { color: "white !important" },
                }}
              >
                <CloseIcon />
              </IconButton>
              {true && (
                <IconButton
                  onClick={handleToggleFullscreen}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                  }}
                  onTouchEnd={(e) => {
                    e.stopPropagation();
                    handleToggleFullscreen();
                  }}
                  size="small"
                  sx={{
                    "&, & *": { color: "white !important" },
                  }}
                >
                  {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
              )}

              {isFullscreen && (
                <IconButton
                  onClick={handlePoiList}
                  size="small"
                  sx={{
                    "&, & *": { color: "white !important" },
                  }}
                >
                  <ListOutlinedIcon />
                </IconButton>
              )}
              {isFullscreen && (
                <>
                  <IconButton
                    onClick={handleZoomIn}
                    size="small"
                    sx={{
                      "&, & *": { color: "white !important" },
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleZoomReset}
                    size="small"
                    sx={{
                      "&, & *": { color: "white !important" },
                    }}
                  >
                    <RestartAltIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleZoomOut}
                    size="small"
                    sx={{
                      "&, & *": { color: "white !important" },
                    }}
                  >
                    <RemoveIcon />
                  </IconButton>
                </>
              )}
            </Box>
            {!isFullscreen && (
              <Box
                position="absolute"
                top={mobile ? "25%" : "45%"}
                left={mobile ? "25%" : "45%"}
                textAlign="center"
                bgcolor={"rgba(128, 128, 128, 0.7)"}
              >
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    p: "20px 40px",
                    bgcolor: "#008080",
                    "&, & *": { color: "white !important" },
                  }}
                  onClick={handleToggleFullscreen}
                >
                  Vai Alla Mappa
                </Button>
              </Box>
            )}
            {isFullscreen && (
              <Box
                sx={{
                  position: "fixed",
                  top: 0,
                  right: 0,
                  width: mobile ? "100%" : "40%",
                  height: "100vh",
                  backgroundColor: "background.paper",
                  boxShadow:
                    "0px 0px 10px rgba(0,0,0,0.6), -10px 0px 10px rgba(0,0,0,0.5)",
                  transform: isPanelOpen ? "translateX(0)" : "translateX(100%)",
                  transition: "transform 0.3s ease-in-out",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {isPoiList && (
                  <>
                    <Box
                      sx={{
                        p: "20px 50px 20px 65px",
                        flexGrow: 1,
                        height: "100vh", // o qualsiasi altezza fissa
                        overflowY: "auto", // abilita lo scroll verticale
                        overflowX: "hidden", // nasconde lo scroll orizzontale
                      }}
                    >
                      <Box>
                        <Typography
                          variant="h5"
                          sx={{
                            color: "#008080",
                            fontWeight: "bold",
                            fontSize: 32,
                            fontFamily: "inherit",
                          }}
                        >
                          {mappa.nome}
                        </Typography>
                        <Typography sx={{ color: "#008080" }}>
                          {mappa.descrizione}
                        </Typography>
                        {mappa.partners && (
                          <>
                            <Typography sx={typographyStyle}>
                              Partners:
                            </Typography>
                            <Typography sx={{ color: "#808080" }}>
                              {mappa.partners}
                            </Typography>
                          </>
                        )}

                        {mappa.collaborazioni && (
                          <>
                            <Typography sx={typographyStyle}>
                              collaborazioni:
                            </Typography>
                            <Typography sx={{ color: "#808080" }}>
                              {mappa.collaborazioni}
                            </Typography>
                          </>
                        )}

                        {mappa.patrocinio && (
                          <>
                            <Typography sx={typographyStyle}>
                              patrocinio:
                            </Typography>
                            <Typography sx={{ color: "#808080" }}>
                              {mappa.patrocinio}
                            </Typography>
                          </>
                        )}

                        {mappa.ringraziamenti && (
                          <>
                            <Typography sx={typographyStyle}>
                              ringraziamenti:
                            </Typography>
                            <Typography sx={{ color: "#808080" }}>
                              {mappa.ringraziamenti}
                            </Typography>
                          </>
                        )}

                        {mappa.sostenitori && (
                          <>
                            <Typography sx={typographyStyle}>
                              sostenitori:
                            </Typography>
                            <Typography sx={{ color: "#808080" }}>
                              {mappa.sostenitori}
                            </Typography>
                          </>
                        )}
                        {mappa.digitalPartner && (
                          <>
                            <Typography sx={typographyStyle}>
                              digitalPartner:
                            </Typography>
                            <Typography sx={{ color: "#808080" }}>
                              {mappa.digitalPartner}
                            </Typography>
                          </>
                        )}

                        {mappa.mediaPartner && (
                          <>
                            <Typography sx={typographyStyle}>
                              mediaPartner:
                            </Typography>
                            <Typography sx={{ color: "#808080" }}>
                              {mappa.mediaPartner}
                            </Typography>
                          </>
                        )}
                      </Box>
                      <Typography sx={typographyStyle}>Elenco POI:</Typography>
                      {mappa.pois.map((poi) => (
                        <Box
                          key={poi.id}
                          sx={{
                            p: 1,
                            display: "flex",
                            justifyContent: "space-between",
                            cursor: "pointer",
                          }}
                          onClick={() => handlePoiClick(poi)}
                        >
                          <Stack direction="row" spacing={2}>
                            {poi.tipoId === 10 && (
                              <AutoStoriesIcon
                                color="inherit"
                                fontSize="medium"
                                sx={{
                                  filter:
                                    "drop-shadow(0px 0px 2px rgba(255,255,255,0.5))",
                                  color: "#008080",
                                }}
                              />
                            )}
                            {poi.tipoId === 11 && (
                              <TipsAndUpdatesIcon
                                color="inherit"
                                fontSize="medium"
                                sx={{
                                  filter:
                                    "drop-shadow(0px 0px 2px rgba(255,255,255,0.5))",
                                  color: "#008080",
                                }}
                              />
                            )}
                            <Typography variant="h6" sx={titoloListStyle}>
                              {poi.titolo}
                            </Typography>
                          </Stack>
                        </Box>
                      ))}
                      <Box
                        sx={{
                          p: 2,
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Button
                          onClick={handlePoiList}
                          style={{
                            background: "#008080",
                            color: "white",
                            padding: 4,
                            borderRadius: 0,
                          }}
                        >
                          <CloseIcon />
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}
                {!isPoiList && selectedPoi && (
                  <>
                    <Box
                      sx={{
                        p: "20px 50px 20px 65px",
                        flexGrow: 1,
                        height: "100vh", // o qualsiasi altezza fissa
                        overflowY: "auto", // abilita lo scroll verticale
                        overflowX: "hidden", // nasconde lo scroll orizzontale
                      }}
                    >
                      <Box
                        sx={{
                          paddingBottom: 2,
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <IconButton
                          onClick={handlePoiList}
                          IconButton
                          style={{
                            color: "#008080",
                            background: "white",
                            padding: "0px 0px",
                            borderRadius: 0,
                          }}
                        >
                          <ListOutlinedIcon />
                        </IconButton>
                        <IconButton
                          onClick={handleClosePanel}
                          IconButton
                          style={{
                            color: "#008080",
                            background: "white",
                            padding: "0px 0px",
                            borderRadius: 0,
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{
                          color: "#008080",
                          fontWeight: "bold",
                          fontSize: 28,
                          textTransform: "uppercase",
                          fontFamily: "inherit",
                        }}
                      >
                        {selectedPoi.titolo}
                      </Typography>

                      {selectedPoi?.immagine && (
                        <img
                          src={`${URL_SERVER}/files${selectedPoi.immagine}`}
                          alt={selectedPoi.titolo}
                          style={{ maxWidth: "100%", height: "auto" }}
                        />
                      )}
                      <Typography
                        variant="body1"
                        sx={{
                          my: 4,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {selectedPoi.descrizione}
                      </Typography>
                      {selectedPoi?.video && (
                        <>
                          <Typography variant="h6" sx={titoloSezioneStyle}>
                            guarda il video
                          </Typography>
                          <YouTube
                            videoId={selectedPoi.video.split("v=")[1]}
                            opts={{
                              height: "390",
                              width: "640",
                              playerVars: {
                                // Opzioni del player
                                autoplay: 0,
                              },
                            }}
                            onReady={() => console.log("Video caricato")}
                          />
                        </>
                      )}
                      {selectedPoi?.audio && (
                        <>
                          <Typography variant="h6" sx={titoloSezioneStyle}>
                            ascolta l'audio
                          </Typography>
                          <AudioComponent
                            src={`${URL_SERVER}/files${selectedPoi.audio}`}
                          />
                        </>
                      )}
                      {selectedPoi?.pdf && (
                        <>
                          <Divider sx={{ my: 4 }} />
                          <Button
                            variant="contained"
                            href={URL_SERVER + "/files" + selectedPoi.pdf}
                            target="_blank"
                            style={{
                              background: "#008080",
                              color: "white",
                              padding: 4,
                              borderRadius: 0,
                            }}
                          >
                            <DownloadIcon />
                            scarica il pdf
                          </Button>
                        </>
                      )}
                      <Divider sx={{ my: 4 }} />

                      {selectedPoi?.tipoId &&
                        InfoPoi(
                          "tipologia",
                          selectedPoi.tipoId === 10
                            ? "enogastronomia"
                            : selectedPoi.tipoId === 11
                            ? "natura"
                            : selectedPoi.tipoId === 12
                            ? "cultura"
                            : selectedPoi.tipoId === 13
                            ? "sport"
                            : "eventi"
                        )}
                      {selectedPoi?.autore &&
                        InfoPoi("autore", selectedPoi.autore)}
                      {selectedPoi?.autoreCopertina &&
                        InfoPoi("autore immagine", selectedPoi.autoreCopertina)}
                      {selectedPoi?.lettore &&
                        InfoPoi("lettore", selectedPoi.lettore)}
                      {selectedPoi?.crediti &&
                        InfoPoi("crediti", selectedPoi.crediti)}
                      {selectedPoi?.tag && InfoPoi("tag", selectedPoi.tag)}

                      <Button
                        onClick={handlePreviousPoi}
                        style={{
                          position: "absolute",
                          left: 0,
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "rgba(0, 128, 128,0.7)",
                          color: "white",
                          padding: "10px",
                          borderRadius: 0,
                        }}
                      >
                        <ArrowBackIosNewIcon />
                      </Button>
                      <Button
                        onClick={handleNextPoi}
                        style={{
                          position: "absolute",
                          right: 0,
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "rgba(0, 128, 128,0.7)",
                          color: "white",
                          padding: "10px",
                          borderRadius: 0,
                        }}
                      >
                        <ArrowForwardIosIcon />
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </>
  );
}

export default App;
